<template>
    <modal-wrapper modalClass="modal" :modalName="name" @before-open="beforeOpen" @modalWasClosed="beforeClose">
        <div class="modal__header">
            <h1 v-if="!template">{{$t('modals.hydrate_document_template.title_pick_template')}}</h1>
            <h1 v-else>{{$t('modals.hydrate_document_template.title_fill_fields', template)}}</h1>
        </div>
        <div class="modal__content">
            <template v-if="!template">
                <p>{{$t('modals.hydrate_document_template.instruction_pick_template')}}</p>
                <document-templates-pick-table @pick="pick"/>
            </template>
            <form v-else class="form" @submit.prevent="submit">
                <!-- Template has been picked, now fill in the fields -->
                <template v-if="template.fields.length">
                    <!-- Template has custom fields that need to be filled in -->
                    <p>{{$t('modals.hydrate_document_template.instruction_fill_fields')}}</p>
                    <div v-for="field in template.fields">
                        <template v-if="field.type === 'text_singleline'">
                            <p><strong>{{field.description}}</strong></p>
                            <div class="form__item">
                                <i class="form__icon icons icon-pencil"></i>
                                <input type="text"
                                       v-model="entries[field.name]"
                                       :class="['form__input--text', {'has-value': entries[field.name]}]"/>
                                <label class="form__label">{{field.name}}</label>
                            </div>
                        </template>
                        <template v-else-if="field.type === 'list'">
                            <p><strong>{{field.description}}</strong></p>
                            <div class="form__item">
                                <div v-for="option in field.configuration" class="checkbox">
                                    <input type="checkbox" v-model="entries[field.name][option.id]"
                                           :id="'checkbox:' + field.name + ':' + option.id"
                                           :name="'checkbox:' + field.name"
                                           class="checkbox__cb"/>
                                    <label :for="'checkbox:' + field.name + ':' + option.id"
                                           class="checkbox__label">
                                        {{option.text}}
                                    </label>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <!-- Template has no custom fields -->
                    <p>{{$t('modals.hydrate_document_template.no_fields')}}</p>
                </template>
            </form>
            <div v-if="uploadErrors.length" class="alert--error">
                <span v-for="err in uploadErrors" :key="err"> {{ err }} </span>
            </div>
            <div v-if="documentUploading">
                <div class="spinner"></div>
            </div>
        </div>
        <div class="modal__footer">
            <div class="actions">
                <button class="actions__action" v-if="template" @click.prevent="unsetTemplate">
                    {{$t('modals.hydrate_document_template.button_goto_template_selection')}}
                </button>
                <button class="actions__action--save pull-right" @click.prevent="submit" :disabled="!canSubmit">
                    {{$t('modals.hydrate_document_template.button_submit')}}
                </button>
            </div>
        </div>
    </modal-wrapper>
</template>
<script>
    import Vue from 'vue';
    import {Component} from 'vue-property-decorator';
    import ModalWrapper from '@/components/general/ModalWrapper';
    import DocumentTemplatesPickTable from '@/components/tables/dashboard/DocumentTemplatesPickTable';
    import {
        DOCUMENT_TEMPLATE_TAG_TYPE_CHOICE,
        DOCUMENT_TEMPLATE_TAG_TYPE_STRING
    } from '@/store/modules/document-template-tags';
    import axios from 'axios';

    @Component({
        components: {
            DocumentTemplatesPickTable,
        }
    })
    export default class DocumentVersionsModal extends Vue {
        name = 'hydrate-template-modal';
        uploadErrors = [];
        documentUploading = false;
        document = {};
        template = null;
        entries = null;
        uploadCancelToken = null;

        get trajectory() {
            return this.$store.state.trajectory.trajectory;
        }

        beforeOpen(event) {
            this.uploadErrors = [];
            this.documentUploading = false;
            this.document = event.params.document;
            this.template = null;
            this.entries = null;
            this.uploadCancelToken = null;
        }

        beforeClose() {
            console.log('[hydrate-template-modal] closing');
            this.unsetTemplate();
            this.cancelUpload();
        }

        pick({template}) {
            console.log('[hydrate-template-modal]', 'pick', template);
            this.template = template;
            const entries = {};
            template.fields.forEach(field => {
                switch (field.type) {
                case DOCUMENT_TEMPLATE_TAG_TYPE_STRING:
                    entries[field.name] = '';
                    break;
                case DOCUMENT_TEMPLATE_TAG_TYPE_CHOICE:
                    // The field configuration is a string array
                    // We setup the entries for choice to be an object indexed by the string values and set booleans
                    entries[field.name] = {};
                    field.configuration.forEach(option => {
                        entries[field.name][option.id] = false;
                    });
                    break;
                }
            });
            this.entries = entries;
        }

        unsetTemplate() {
            console.log('[hydrate-template-modal]', 'unset template');
            this.template = null;
            this.entries = null;
        }

        cancelUpload() {
            if (this.uploadCancelToken) {
                console.log('[hydrate-template-modal] cancelling upload');
                this.uploadCancelToken.cancel('modal closed');
            }
        }

        submit() {
            console.log('[hydrate-template-modal]', 'submit', 'start');
            this.uploadErrors = [];
            const payload = {
                document_template_id: this.template.id,
                fields: [],
            };
            this.template.fields.forEach(field => {
                let value;
                switch (field.type) {
                case DOCUMENT_TEMPLATE_TAG_TYPE_STRING:
                    value = this.entries[field.name];
                    break;
                case DOCUMENT_TEMPLATE_TAG_TYPE_CHOICE:
                    value = JSON.stringify(Object.keys(this.entries[field.name])
                        .map(key => this.entries[field.name][key] ? key : false)
                        .filter(idOrNull => idOrNull));
                    break;
                }
                payload.fields.push({
                    document_template_field_id: field.id,
                    value
                });
            });
            this.documentUploading = true;
            this.uploadCancelToken = axios.CancelToken.source();
            this.axios.post('/documents', payload, {cancelToken: this.uploadCancelToken.token})
                .then(res => {
                    this.$notify.success(this.$t('notify.hydrate_document_template.success'));
                    this.$emit('hydrated', res.data);
                    this.$modal.hide(this.name);
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        console.log('[hydrate-template-modal] submit cancelled');
                    } else {
                        console.log('[hydrate-template-modal] submit failure', err);
                        if (err.status === 422 && err.errors) {
                            this.uploadErrors = err.errors;
                            this.$notify.error(this.$t('common.validation_error'));
                        } else {
                            this.$notify.error(this.$t('notify.hydrate_document_template.error'));
                        }
                    }
                })
                .then(() => {
                    this.documentUploading = false;
                    this.uploadCancelToken = null;
                });
        }

        get canSubmit() {
            if (!this.template) return false;
            if (!this.template.fields.length) return true;
            return this.template.fields.every(field => {
                const data = this.entries[field.name];
                switch (field.type) {
                case DOCUMENT_TEMPLATE_TAG_TYPE_STRING:
                    return data.length > 0;
                case DOCUMENT_TEMPLATE_TAG_TYPE_CHOICE:
                    return Object.keys(data).some(key => data[key]);
                }
            });
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/modals/dashboard/HydrateTemplateModal.vue